import { FilterOption, FilterTypes } from 'features/filters/types'

/**
 * Does this `FilterOption` carry a real payload, or is it a name/alias-only
 * stub? A per-field `/filter/options/v2` response returns full data for the
 * requested fields and stubs (`{field, alias}`, no `values` / `min` / `max`)
 * for every other field — see the route's `_superset_filter_options`, which
 * only `option.update(item)`s fields present in the request's `fields`.
 *
 * Classifying by PAYLOAD rather than by "was this field in the request's
 * `fields`" is load-bearing, not incidental: `_superset_filter_options` also
 * appends **editable custom fields** with their full `values`, unconditionally
 * and after the stub branch, so those arrive populated even when unrequested.
 * Simplifying this to a `fields.includes(option.field)` check would discard
 * their counts with nothing failing.
 */
const isPopulated = (option: FilterOption): boolean =>
  option.filter_type === FilterTypes.SELECT
    ? Array.isArray(option.values)
    : option.min !== undefined && option.max !== undefined

/**
 * Overlay the counted fields of a per-field V2 response onto `base`.
 *
 * `base` is the names-only list (`stripFilterCounts(defaultFilterList)`) and
 * owns the field set + every field's values; `response` wins per field where
 * it carries a real payload. Stub entries are dropped rather than merged, so
 * expanding one popover field cannot blank out the other fields' values (the
 * applied filter cards and the popover's field list both read the same list).
 *
 * Fields present only in `response` are ignored: `base` is what the popover's
 * "All Properties" view renders, so the two views stay on one field set. That
 * is only safe because of a backend invariant this file cannot check —
 * `/post/filter/options` (which fills `base`) and `/post/filter/options/v2`
 * resolve through the same `GetFilterOptionsUseCase`, off the same per-project
 * superset cache, with the same hidden-field filtering, so the response's
 * field set is a subset of the base's. If the two routes ever diverge, a
 * response-only field would silently vanish from the popover.
 */
export const overlayFilterCounts = (
  base: FilterOption[],
  response: FilterOption[],
): FilterOption[] => {
  const counted = new Map(
    response.filter(isPopulated).map((option) => [option.field, option]),
  )
  if (counted.size === 0) return base
  return base.map((option) => counted.get(option.field) ?? option)
}
